<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { getLottieData } from '@/icons/lottie';
interface Props {
  size?: number | string;
  color?: string;
}
const props = defineProps<Props>();
const container = ref<HTMLElement | null>(null);
type LottieModule = {
  loadAnimation: (config: Record<string, unknown>) => {
    destroy: () => void;
  };
};
let lottieModule: LottieModule | null = null;
let lottieAnimation: {
  destroy: () => void;
} | null = null;
const sizeStyle = computed(() => {
  if (props.size === undefined) {
    return '20px';
  }
  return typeof props.size === 'number' ? `${props.size}px` : props.size;
});
async function loadLottieModule() {
  if (lottieModule) {
    return lottieModule;
  }
  const module = await import('lottie-web');
  const normalizedModule = module as unknown as {
    default?: LottieModule;
  };
  lottieModule = normalizedModule.default ?? (module as unknown as LottieModule);
  return lottieModule;
}
function destroyLottie() {
  if (lottieAnimation) {
    lottieAnimation.destroy();
    lottieAnimation = null;
  }
  if (container.value) {
    container.value.innerHTML = '';
  }
}
async function renderLottie() {
  if (!container.value) {
    return;
  }
  const resolvedColor = props.color ?? getComputedStyle(container.value).color;
  const data = await getLottieData('loader', resolvedColor);
  if (!data) {
    destroyLottie();
    return;
  }
  destroyLottie();
  const { loadAnimation } = await loadLottieModule();
  lottieAnimation = loadAnimation({
    container: container.value,
    renderer: 'svg',
    loop: true,
    autoplay: true,
    animationData: data as Record<string, unknown>,
  });
}
watch(
  () => props.color,
  async () => {
    await nextTick();
    await renderLottie();
  },
);
onMounted(async () => {
  await nextTick();
  await renderLottie();
});
onBeforeUnmount(() => {
  destroyLottie();
});
</script>

<template>
  <span ref="container" :class="$style.root" :style="{ width: sizeStyle, height: sizeStyle }" />
</template>

<style lang="scss" module>
.root {
  display: inline-flex;
  width: 20px;
  height: 20px;
}
</style>
